/**
 * useOrderPickup Composable
 * Handles order pickup state and business logic (Single Responsibility Principle)
 * Compatible with Vue 2
 */

import { submitOrderPickup } from "@/services/orderPickupService";
import { fetchStoreInfo } from "@/services/storeService";
import { transformStoreInfo } from "@/transformers/storeTransformer";
import { resolveStoreAlias } from "@/helpers/urlHelper";

/**
 * Composable for managing order pickup operations
 * @param {Object} store - Vuex store instance
 * @param {Object} router - Vue router instance
 * @param {Object} route - Vue route instance
 * @param {Object} componentState - Component's reactive state object (from data())
 * @returns {Object} Order pickup methods
 */
export function useOrderPickup(store, router, route, componentState) {
  if (!store) {
    console.warn("useOrderPickup: Store instance is required");
    return {
      initialize: async () => {},
      submitPickup: async () => {}
    };
  }

  if (!componentState) {
    console.warn("useOrderPickup: Component state is required");
    return {
      initialize: async () => {},
      submitPickup: async () => {}
    };
  }

  /**
   * Initializes the order pickup page
   * Loads store info and sets up route parameters
   */
  async function initialize() {
    try {
      componentState.loading = true;
      componentState.error = null;

      // Get route parameters
      componentState.token = route.query.token || null;
      componentState.storeAlias = resolveStoreAlias(route);
      componentState.orderId = route.params.id;

      if (!componentState.storeAlias || !componentState.orderId) {
        throw new Error("Store alias and order ID are required");
      }

      // Set store alias in Vuex
      await store.dispatch("setStoreAlias", componentState.storeAlias);

      // Fetch store info
      const storeInfoResponse = await fetchStoreInfo(componentState.storeAlias);

      if (storeInfoResponse.status === 404) {
        if (router) {
          await router.push({ name: "NotFound" });
        }
        return;
      }

      if (!storeInfoResponse.ok) {
        throw new Error(
          `Failed to fetch store info: ${storeInfoResponse.status}`
        );
      }

      const rawData = await storeInfoResponse.json();
      const transformedData = transformStoreInfo(rawData);

      // Set base URL in store
      if (transformedData.dashboard_link) {
        await store.dispatch("setBaseUrl", transformedData.dashboard_link);
      }

      componentState.loading = false;
    } catch (err) {
      componentState.error =
        err?.data?.message || err?.message || "An error occurred";
      componentState.loading = false;
      throw err;
    }
  }

  /**
   * Submits the order pickup notification
   */
  async function submitPickup() {
    if (!componentState.carModel || !componentState.parkingNumber) {
      return;
    }

    try {
      componentState.submitting = true;
      componentState.error = null;

      await submitOrderPickup({
        orderId: componentState.orderId,
        carModel: componentState.carModel,
        parkingNumber: componentState.parkingNumber,
        token: componentState.token
      });

      componentState.submitted = true;
      componentState.submitting = false;
    } catch (err) {
      componentState.error =
        err?.data?.message ||
        err?.message ||
        "An error occurred while notifying staff";
      componentState.submitting = false;
      throw err;
    }
  }

  return {
    // Methods
    initialize,
    submitPickup
  };
}
